import PropTypes from "prop-types"
import { Add, ChatBubbleOutline, Delete, Edit, MoreVert } from "@mui/icons-material"
import { Fab, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem } from "@mui/material"
import moment from "moment"
import { useState } from "react"
import { useThreadsContext } from "../../hooks/context"
import AiTeammateFetchingProgress from "../fetching-progress"
import "./style.scss"
import ThreadRenameField from "./thread-rename-field"
import { useAccountAlias } from "resources/account"

export default function AiTeammateThreadsList({ title = "Chat History", showNewChatButton = true, showFetchingProgress = true, showUpdatedAt = true }) {
  const accountAlias = useAccountAlias()
  const { threads, isFetching, openThread, newThread, setThreadIdToDelete, updateThread, isUpdatingThread, threadId: activeThreadId } = useThreadsContext()
  const [anchorEl, setAnchorEl] = useState(null)
  const [threadIdToRename, setThreadIdToRename] = useState(null)

  function handleCloseMenu() {
    setAnchorEl(null)
  }

  function handleDelete() {
    const threadId = anchorEl?.dataset?.threadId
    if (!threadId) {
      return
    }
    setThreadIdToDelete(threadId)
    handleCloseMenu()
  }

  function handleRename() {
    const threadId = anchorEl?.dataset?.threadId
    if (!threadId) {
      return
    }
    setThreadIdToRename(threadId)
    handleCloseMenu()
  }

  async function renameThread(threadId, title) {
    await updateThread({ accountAlias, threadId, title })
    setThreadIdToRename(null)
  }

  function handleCancelRename() {
    setThreadIdToRename(null)
  }

  return <div className="ai-teammate-threads-list">
    {showFetchingProgress && <AiTeammateFetchingProgress isFetching={isFetching} />}
    {threads && <List
      className="threads-list"
      subheader={<ListSubheader className="threads-list-subheader" disableSticky>
        {title}
      </ListSubheader>}
    >
      {threads.map(({ threadId, title, updatedAt }) => {
        if (threadId === threadIdToRename) {
          return <ListItem key={threadId}>
            <ThreadRenameField initialTitle={title} onCancel={handleCancelRename} onSave={(newTitle) => renameThread(threadId, newTitle)} isSaving={isUpdatingThread} />
          </ListItem>
        }
        return <ListItemButton key={threadId} selected={threadId === activeThreadId} onClick={() => openThread(threadId, title)}>
          <ListItemIcon className="thread-list-item-icon"><ChatBubbleOutline fontSize="small" /></ListItemIcon>
          <ListItemText className="thread-list-item-text" primary={title} secondary={showUpdatedAt ? moment(updatedAt).fromNow() : undefined} />
          <IconButton
            data-thread-id={threadId}
            onClick={(e) => {
              e.stopPropagation()
              setAnchorEl(e.currentTarget)
            }}
            edge="end"
            aria-label="delete"
          >
            <MoreVert fontSize="small" />
          </IconButton>
        </ListItemButton>
      })}
    </List>}
    {showNewChatButton && <Fab onClick={newThread} size="medium" className="new-chat-fab" variant="extended" color="primary" aria-label="New Chat">
      <Add />
      <span className="new-chat-fab-text">New Chat</span>
    </Fab>}
    <Menu
      anchorEl={anchorEl}
      open={!!anchorEl}
      onClose={handleCloseMenu}
    >
      <MenuItem onClick={handleRename}>
        <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
        <ListItemText>Rename</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleDelete} className="danger">
        <ListItemIcon sx={{ color: "inherit" }}><Delete fontSize="small" /></ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>
    </Menu>
  </div>
}

AiTeammateThreadsList.propTypes = {
  title: PropTypes.string,
  showNewChatButton: PropTypes.bool,
  showFetchingProgress: PropTypes.bool,
  showUpdatedAt: PropTypes.bool,
}
